import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { getTaxSlabs, updateTaxSlab } from "../../services";

const defaultTaxSlabs = [
  { id: 1, min_income: 0, max_income: 600000, tax_rate: 0, fixed_tax: 0 },
  { id: 2, min_income: 600001, max_income: 1200000, tax_rate: 1, fixed_tax: 0 },
  { id: 3, min_income: 1200001, max_income: 2200000, tax_rate: 11, fixed_tax: 6000 },
  { id: 4, min_income: 2200001, max_income: 3200000, tax_rate: 23, fixed_tax: 116000 },
  { id: 5, min_income: 3200001, max_income: 4100000, tax_rate: 30, fixed_tax: 346000 },
  { id: 6, min_income: 4100001, max_income: null, tax_rate: 35, fixed_tax: 616000 },
];

export default function TaxSlabModal({ isOpen, onClose, slabs = defaultTaxSlabs }) {
  const [rows, setRows] = useState(slabs);
  const [draftRows, setDraftRows] = useState(slabs);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchTaxSlabs = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await getTaxSlabs();
        const result = response?.data?.Result;
        // Backend can return array directly or wrapped object
        const list = Array.isArray(result)
          ? result
          : Array.isArray(result?.TaxSlabs)
          ? result.TaxSlabs
          : Array.isArray(response?.data)
          ? response.data
          : [];

        const resolvedRows = list.length > 0 ? list : slabs;
        setRows(resolvedRows);
        setDraftRows(resolvedRows);
      } catch (err) {
        console.error("Failed to fetch tax slabs", err);
        setError("Failed to load tax slabs.");
        setRows(slabs);
        setDraftRows(slabs);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTaxSlabs();
  }, [isOpen, slabs]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120]"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

        <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl rounded-2xl border border-gray-200 dark:border-blue-gray-900 bg-white dark:bg-[#0a0a0a] shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-black text-gray-900 dark:text-white">Tax Slabs</h3>
              <div className="flex items-center gap-2">
                {!isEditMode ? (
                  <button
                    type="button"
                    onClick={() => setIsEditMode(true)}
                    className="px-3 py-1.5 rounded-lg text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                    disabled={isLoading}
                  >
                    Update
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setDraftRows(rows);
                        setIsEditMode(false);
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm font-bold bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (isSaving) return;
                        setError("");
                        setIsSaving(true);
                        try {
                          const payloadRows = draftRows.map((r, idx) => ({
                            id: Number(r.id ?? idx + 1),
                            min_income: Number(r.min_income ?? r.minIncome ?? 0),
                            max_income:
                              r.max_income === "" ||
                              r.max_income === undefined ||
                              r.max_income === null
                                ? null
                                : Number(r.max_income ?? r.maxIncome ?? 0),
                            tax_rate: Number(r.tax_rate ?? r.taxRate ?? 0),
                            fixed_tax: Number(r.fixed_tax ?? r.fixedTax ?? 0),
                          }));

                          // Frontend guard: max must be >= min when provided
                          const invalidRow = payloadRows.find(
                            (row) =>
                              row.max_income !== null &&
                              Number(row.max_income) < Number(row.min_income)
                          );
                          if (invalidRow) {
                            setError(
                              `Validation failed on slab id ${invalidRow.id}: max_income cannot be less than min_income`
                            );
                            setIsSaving(false);
                            return;
                          }

                          // Save each slab row through backend update API
                          await Promise.all(payloadRows.map((row) => updateTaxSlab(row)));

                          setRows(draftRows);
                          setIsEditMode(false);
                        } catch (err) {
                          console.error("Failed to update tax slabs", err);
                          setError("Failed to save tax slabs. Please try again.");
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  aria-label="Close tax slab modal"
                  disabled={isSaving}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="overflow-x-auto rounded-xl border border-gray-300 dark:border-gray-700">
                <table className="min-w-[760px] w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr className="text-left">
                      <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700">id</th>
                      <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700">min_income</th>
                      <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700">max_income</th>
                      <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700">tax_rate</th>
                      <th className="px-3 py-2 border-b border-gray-300 dark:border-gray-700">fixed_tax</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-black/20">
                    {isLoading ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                          Loading tax slabs...
                        </td>
                      </tr>
                    ) : rows.length > 0 ? (
                      (isEditMode ? draftRows : rows).map((row, idx) => (
                      <tr key={row.id} className="border-b border-gray-200 dark:border-gray-800">
                        <td className="px-3 py-2 text-gray-800 dark:text-gray-100">{row.id ?? idx + 1}</td>
                        <td className="px-3 py-2 text-gray-800 dark:text-gray-100 tabular-nums">
                          {isEditMode ? (
                            <input
                              type="number"
                              value={row.min_income ?? row.minIncome ?? 0}
                              onChange={(e) =>
                                setDraftRows((prev) =>
                                  prev.map((r, i) =>
                                    i === idx ? { ...r, min_income: Number(e.target.value) } : r
                                  )
                                )
                              }
                              className="w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded px-2 py-1"
                            />
                          ) : (
                            row.min_income ?? row.minIncome ?? 0
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-800 dark:text-gray-100 tabular-nums">
                          {isEditMode ? (
                            <input
                              type="number"
                              value={
                                row.max_income === null ||
                                row.max_income === undefined
                                  ? ""
                                  : (row.max_income ?? row.maxIncome)
                              }
                              onChange={(e) =>
                                setDraftRows((prev) =>
                                  prev.map((r, i) =>
                                    i === idx
                                      ? {
                                          ...r,
                                          max_income:
                                            e.target.value === ""
                                              ? null
                                              : Number(e.target.value),
                                        }
                                      : r
                                  )
                                )
                              }
                              className="w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded px-2 py-1"
                            />
                          ) : (row.max_income ?? row.maxIncome) === null ? (
                            "NULL"
                          ) : (
                            row.max_income ?? row.maxIncome ?? 0
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-800 dark:text-gray-100 tabular-nums">
                          {isEditMode ? (
                            <input
                              type="number"
                              value={row.tax_rate ?? row.taxRate ?? 0}
                              onChange={(e) =>
                                setDraftRows((prev) =>
                                  prev.map((r, i) =>
                                    i === idx ? { ...r, tax_rate: Number(e.target.value) } : r
                                  )
                                )
                              }
                              className="w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded px-2 py-1"
                            />
                          ) : (
                            row.tax_rate ?? row.taxRate ?? 0
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-800 dark:text-gray-100 tabular-nums">
                          {isEditMode ? (
                            <input
                              type="number"
                              value={row.fixed_tax ?? row.fixedTax ?? 0}
                              onChange={(e) =>
                                setDraftRows((prev) =>
                                  prev.map((r, i) =>
                                    i === idx ? { ...r, fixed_tax: Number(e.target.value) } : r
                                  )
                                )
                              }
                              className="w-full bg-transparent border border-gray-300 dark:border-gray-700 rounded px-2 py-1"
                            />
                          ) : (
                            Number(row.fixed_tax ?? row.fixedTax ?? 0).toFixed(2)
                          )}
                        </td>
                      </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                          {error || "No tax slabs found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {error && (
                <p className="mt-3 text-sm text-rose-600 dark:text-rose-300">{error}</p>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

